import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isNullOrEmpty, trimValue } from '../../../actions/helper';
import type { AssetListDetailProperties } from '../../../containers/assets/details/AssetsListDetail.container';
import { AssetsListDetailTab } from '../../../containers/assets/details/types';
import { closableSuccessNotification } from '../ClickableNotifications';
import { type Asset, AssetStatus, IdentificationType } from '../types';
import { ArchiveAssetConfirmationDialog } from './ArchiveAssetConfirmationDialog';
import { AssetDetails, DATE_FORMAT } from './AssetDetails';
import { DeviceList } from './DeviceList';
import { SidebarFooter } from './SidebarFooter';
import { TagManagerWrapper } from './TagManagerWrapper';
import {
    endDateIsSameOrAfterStartDateOrNull,
    isAssetIdentificationValid,
    numberHasChanged,
    stringHasChanged,
} from './assetDetailsHelper';
import { type SelectedTag, SelectedTagStatus } from './types';
import { useIsLatamTenant } from '../../../../../configuration/login/loginSlice';
import { useGetActiveContractsQuery } from '../../../../services/contractsApi';
import { useCheckActiveContractsQuery } from '../../../../services/latamContractsApi';

const SIDEBAR_BREAKPOINT = 1440;

export type AssetsListDetailsProps = AssetListDetailProperties;

export const AssetsListDetail = (props: AssetsListDetailsProps) => {
    const {
        availableTags,
        asset,
        assetDeleting,
        assetTagsUpdating,
        assetUpdating,
        changeAssetDetailsTab,
        currentAssetDetailsTab,
        closeDeleteDialog,
        createAssetTags,
        deleteAsset,
        deletionErrorMessageCode,
        deletionSuccessful,
        displayDeleteDialog,
        associatedDevices,
        onClose,
        openDeleteDialog,
        toggleConfirmDialog,
        toggleUnsavedAssetChanges,
        updateAsset,
        updateMasterdata,
        updateAssetTags,
        resetAssetDeletionState,
    } = props;

    const intl = useIntl();

    const [currentAssetName, setCurrentAssetName] = useState<string>(asset.name);
    const [currentAssetIdentification, setCurrentAssetIdentification] = useState(asset.identification);
    const [currentAssetBrand, setCurrentAssetBrand] = useState(asset.brand);
    const [currentAssetLicensePlate, setCurrentAssetLicensePlate] = useState<string | null>(asset.license_plate);
    const [currentAssetLicensePlateCountryCode, setCurrentAssetLicensePlateCountryCode] = useState<string | null>(
        asset.license_plate_country_code
    );
    const [identificationHasError, setIdentificationHasError] = useState(false);
    const [unsavedTags, setUnsavedTags] = useState<SelectedTag[]>([]);
    const [showArchiveAssetConfirmationDialog, setShowArchiveAssetConfirmationDialog] = useState<boolean>(false);
    // Masterdata
    const [currentMasterdataAdr, setMasterdataAdr] = useState(asset.masterdata?.adr);
    const [currentMasterdataAirFreightSuitability, setMasterdataAirFreightSuitability] = useState(
        asset.masterdata?.air_freight_suitability
    );
    const [currentMasterdataComment, setMasterdataComment] = useState(asset.masterdata?.comment);
    const [currentMasterdataCustomsBond, setMasterdataCustomsBond] = useState(asset.masterdata?.customs_bond);
    const [currentMasterdataCostCenter, setMasterdataCostCenter] = useState(asset.masterdata?.cost_center);
    const [currentMasterdataFuelType, setMasterdataFuelType] = useState(asset.masterdata?.fuel_type);
    const [currentMasterdataLeasingRate, setMasterdataLeasingRate] = useState(
        asset.masterdata?.ownership?.contract_rate
    );
    const [currentMasterdataNumberOfAxes, setMasterdataNumberOfAxes] = useState(asset.masterdata?.number_of_axes);
    const [currentMasterdataVehicleModel, setMasterdataVehicleModel] = useState(asset.masterdata?.vehicle_model);
    const [currentMasterdataEngineType, setMasterdataEngineType] = useState(asset.masterdata?.engine_type);
    const [currentMasterdataOwnershipEndDate, setMasterdataOwnershipEndDate] = useState<string | null | undefined>(
        asset.masterdata?.ownership?.ownership_end_at ?? undefined
    );
    const [currentMasterdataOwnershipEndDateChangeJustTriggered, setMasterdataOwnershipEndDateChangeJustTriggered] =
        useState(false);
    const [currentMasterdataOwnershipStartDate, setMasterdataOwnershipStartDate] = useState<string | null | undefined>(
        asset.masterdata?.ownership?.ownership_start_at ?? undefined
    );
    const [currentMasterdataOwnershipTermInMonths, setMasterdataOwnershipTermInMonths] = useState(
        asset.masterdata?.ownership?.contract_duration_in_months
    );
    const [
        currentMasterdataOwnershipTermInMonthsChangeJustTriggered,
        setMasterdataOwnershipTermInMonthsChangeJustTriggered,
    ] = useState(false);
    const [currentMasterdataOwnershipType, setMasterdataOwnershipType] = useState(asset.masterdata?.ownership?.type);
    const [currentMasterdataReference, setMasterdataReference] = useState(asset.masterdata?.ownership?.reference);
    const [currentMasterdataRegisteredFirstAt, setMasterdataRegisteredFirstAt] = useState<string | null | undefined>(
        asset.masterdata?.ownership?.registered_first_at ?? undefined
    );
    const [currentMasterdataTires, setMasterdataTires] = useState(asset.masterdata?.tires);

    const hasUnsavedChanges = useCallback(() => {
        const hasLicensePlateChanged = stringHasChanged(currentAssetLicensePlate, asset.license_plate);
        const hasLicensePlateCountryCodeChanged = stringHasChanged(
            currentAssetLicensePlateCountryCode,
            asset.license_plate_country_code
        );
        const hasNameChanged = stringHasChanged(currentAssetName, asset.name);
        const hasIdentificationChanged = stringHasChanged(currentAssetIdentification, asset.identification);
        const hasTagsChanged = unsavedTags.length > 0;
        const hasMasterdataAdrChanged = stringHasChanged(currentMasterdataAdr, asset.masterdata?.adr);
        const hasMasterdataAirFreightSuitabilityChanged = stringHasChanged(
            currentMasterdataAirFreightSuitability,
            asset.masterdata?.air_freight_suitability
        );
        const hasMasterdataCommentChanged = stringHasChanged(currentMasterdataComment, asset.masterdata?.comment);
        const hasMasterdataCustomsBondChanged = stringHasChanged(
            currentMasterdataCustomsBond,
            asset.masterdata?.customs_bond
        );
        const hasMasterdataCostCenterChanged = stringHasChanged(
            currentMasterdataCostCenter,
            asset.masterdata?.cost_center
        );
        const hasMasterdataFuelTypeChanged = stringHasChanged(currentMasterdataFuelType, asset.masterdata?.fuel_type);
        const hasMasterdataLeasingRateChanged = numberHasChanged(
            currentMasterdataLeasingRate,
            asset.masterdata?.ownership?.contract_rate
        );
        const hasMasterdataNumberOfAxesChanged = numberHasChanged(
            currentMasterdataNumberOfAxes,
            asset.masterdata?.number_of_axes
        );
        const hasMasterdataOwnershipTypeChanged = stringHasChanged(
            currentMasterdataOwnershipType,
            asset.masterdata?.ownership?.type
        );
        const hasMasterdataOwnershipEndDateChanged = stringHasChanged(
            currentMasterdataOwnershipEndDate,
            asset.masterdata?.ownership?.ownership_end_at
        );
        const hasMasterdataOwnershipStartDateChanged = stringHasChanged(
            currentMasterdataOwnershipStartDate,
            asset.masterdata?.ownership?.ownership_start_at
        );
        const hasMasterdataReferenceChanged = stringHasChanged(
            currentMasterdataReference,
            asset.masterdata?.ownership?.reference
        );
        const hasMasterdataRegisteredFirstAtChanged = stringHasChanged(
            currentMasterdataRegisteredFirstAt,
            asset.masterdata?.ownership?.registered_first_at
        );
        const hasMasterdataOwnershipTermInMonthsChanged = numberHasChanged(
            currentMasterdataOwnershipTermInMonths,
            asset.masterdata?.ownership?.contract_duration_in_months
        );
        const hasMasterdataVehicleModelChanged = stringHasChanged(
            currentMasterdataVehicleModel,
            asset.masterdata?.vehicle_model
        );
        const hasMasterdataEngineTypeChanged = stringHasChanged(
            currentMasterdataEngineType,
            asset.masterdata?.engine_type
        );
        const hasMasterdataTiresChanged = stringHasChanged(currentMasterdataTires, asset.masterdata?.tires);
        const hasMasterdataChanged: boolean =
            hasMasterdataAdrChanged ||
            hasMasterdataAirFreightSuitabilityChanged ||
            hasMasterdataCommentChanged ||
            hasMasterdataCustomsBondChanged ||
            hasMasterdataCostCenterChanged ||
            hasMasterdataFuelTypeChanged ||
            hasMasterdataLeasingRateChanged ||
            hasMasterdataNumberOfAxesChanged ||
            hasMasterdataOwnershipTypeChanged ||
            hasMasterdataOwnershipEndDateChanged ||
            hasMasterdataOwnershipStartDateChanged ||
            hasMasterdataReferenceChanged ||
            hasMasterdataRegisteredFirstAtChanged ||
            hasMasterdataOwnershipTermInMonthsChanged ||
            hasMasterdataTiresChanged ||
            hasMasterdataEngineTypeChanged ||
            hasMasterdataVehicleModelChanged;

        return (
            hasNameChanged ||
            hasIdentificationChanged ||
            hasLicensePlateChanged ||
            hasLicensePlateCountryCodeChanged ||
            hasTagsChanged ||
            hasMasterdataChanged
        );
    }, [
        asset,
        currentAssetLicensePlate,
        currentAssetLicensePlateCountryCode,
        currentAssetName,
        currentAssetIdentification,
        currentMasterdataAdr,
        currentMasterdataAirFreightSuitability,
        currentMasterdataComment,
        currentMasterdataCustomsBond,
        currentMasterdataCostCenter,
        currentMasterdataFuelType,
        currentMasterdataLeasingRate,
        currentMasterdataNumberOfAxes,
        currentMasterdataOwnershipEndDate,
        currentMasterdataOwnershipStartDate,
        currentMasterdataOwnershipType,
        currentMasterdataReference,
        currentMasterdataRegisteredFirstAt,
        currentMasterdataOwnershipTermInMonths,
        currentMasterdataTires,
        currentMasterdataVehicleModel,
        currentMasterdataEngineType,
        unsavedTags,
    ]);

    useEffect(() => {
        toggleUnsavedAssetChanges(hasUnsavedChanges());
    }, [toggleUnsavedAssetChanges, hasUnsavedChanges]);

    const resetState = useCallback((assetToReset: Asset) => {
        setCurrentAssetLicensePlate(assetToReset.license_plate);
        setCurrentAssetLicensePlateCountryCode(assetToReset.license_plate_country_code);
        setCurrentAssetName(assetToReset.name);
        setCurrentAssetIdentification(assetToReset.identification);
        setCurrentAssetBrand(assetToReset.brand);
        setMasterdataAdr(assetToReset.masterdata?.adr ?? undefined);
        setMasterdataAirFreightSuitability(assetToReset.masterdata?.air_freight_suitability ?? undefined);
        setMasterdataComment(assetToReset.masterdata?.comment ?? undefined);
        setMasterdataCustomsBond(assetToReset.masterdata?.customs_bond ?? undefined);
        setMasterdataCostCenter(assetToReset.masterdata?.cost_center ?? undefined);
        setMasterdataFuelType(assetToReset.masterdata?.fuel_type ?? undefined);
        setMasterdataLeasingRate(assetToReset.masterdata?.ownership?.contract_rate ?? undefined);
        setMasterdataNumberOfAxes(assetToReset.masterdata?.number_of_axes ?? undefined);
        setMasterdataOwnershipEndDate(assetToReset.masterdata?.ownership?.ownership_end_at ?? undefined);
        setMasterdataOwnershipStartDate(assetToReset.masterdata?.ownership?.ownership_start_at ?? undefined);
        setMasterdataOwnershipType(assetToReset.masterdata?.ownership?.type ?? undefined);
        setMasterdataReference(assetToReset.masterdata?.ownership?.reference ?? undefined);
        setMasterdataRegisteredFirstAt(assetToReset.masterdata?.ownership?.registered_first_at ?? undefined);
        setMasterdataOwnershipTermInMonths(
            assetToReset.masterdata?.ownership?.contract_duration_in_months ?? undefined
        );
        setMasterdataTires(assetToReset.masterdata?.tires ?? undefined);
        setMasterdataVehicleModel(assetToReset.masterdata?.vehicle_model ?? undefined);
        setMasterdataEngineType(assetToReset.masterdata?.engine_type ?? undefined);
        setUnsavedTags([]);
        setIdentificationHasError(false);
    }, []);

    useEffect(() => {
        resetState(asset);
    }, [asset.id, resetState]);

    useEffect(() => {
        //
        if (deletionSuccessful) {
            closableSuccessNotification(<FormattedMessage id={'intl-msg:delete.success'} />);
            resetAssetDeletionState();
            changeAssetDetailsTab(AssetsListDetailTab.ASSET_DETAILS);
            onClose();
        }
    }, [changeAssetDetailsTab, deletionSuccessful, onClose, resetAssetDeletionState]);

    const closeSidebar = useCallback(() => {
        changeAssetDetailsTab(AssetsListDetailTab.ASSET_DETAILS);
        onClose();
    }, [changeAssetDetailsTab, onClose]);

    const onCloseCallback = () => {
        if (hasUnsavedChanges()) {
            toggleConfirmDialog(true, null, closeSidebar);
        } else {
            closeSidebar();
        }
    };

    const isCurrentAssetNameEmpty = currentAssetName?.trim().length === 0;

    const isLatamTenant = useIsLatamTenant() || false;
    const { data: activeContracts } = useGetActiveContractsQuery(asset.id, { skip: isLatamTenant });
    const { data: activeLatamContracts } = useCheckActiveContractsQuery(asset.id, { skip: !isLatamTenant });
    const disallowAssetArchiveWithActiveContracts =
        isLatamTenant &&
        asset.status === AssetStatus.active &&
        [activeContracts, activeLatamContracts].some(c => {
            return Array.isArray(c) && c.length > 0;
        });
    const hasInvalidDetailState =
        isCurrentAssetNameEmpty ||
        assetUpdating ||
        assetTagsUpdating ||
        !isAssetIdentificationValid(currentAssetIdentification);

    const isSaveButtonDisabled = !hasUnsavedChanges() || hasInvalidDetailState;
    const isStatusActionConfirmDisabled =
        hasInvalidDetailState || (asset.status === AssetStatus.active && disallowAssetArchiveWithActiveContracts);
    const isStatusActionDisabled =
        asset.status === AssetStatus.active
            ? assetUpdating || assetTagsUpdating || disallowAssetArchiveWithActiveContracts
            : hasInvalidDetailState;

    const getUpdatedAsset = (status = asset.status): Asset => ({
        ...asset,
        name: currentAssetName ?? '',
        identification_type: currentAssetIdentification ? IdentificationType.vin : null,
        identification: currentAssetIdentification,
        brand: currentAssetBrand,
        license_plate:
            currentAssetLicensePlate && currentAssetLicensePlate.length > 0 ? currentAssetLicensePlate.trim() : null,
        license_plate_country_code: currentAssetLicensePlateCountryCode,
        status,
        tags: unsavedTags.filter(tag => tag.status !== SelectedTagStatus.removed).map(tag => tag.id),
    });

    const submitButtonClick = () => {
        void persistAssetChanges();
    };

    const performMasterdataUpdate = (assetToUpdate: Asset) => {
        return updateMasterdata(assetToUpdate, asset.masterdata, {
            adr: trimValue(currentMasterdataAdr),
            air_freight_suitability: trimValue(currentMasterdataAirFreightSuitability),
            comment: trimValue(currentMasterdataComment),
            customs_bond: trimValue(currentMasterdataCustomsBond),
            cost_center: trimValue(currentMasterdataCostCenter),
            fuel_type: currentMasterdataFuelType,
            number_of_axes: currentMasterdataNumberOfAxes,
            plug_and_charge_ids: undefined,
            tires: trimValue(currentMasterdataTires),
            vehicle_model: currentMasterdataVehicleModel,
            engine_type: currentMasterdataEngineType,
            tachograph: asset.masterdata?.tachograph,
            ownership: {
                contract_duration_in_months: currentMasterdataOwnershipTermInMonths,
                contract_rate: currentMasterdataLeasingRate,
                ownership_end_at: currentMasterdataOwnershipEndDate,
                ownership_start_at: currentMasterdataOwnershipStartDate,
                reference: trimValue(currentMasterdataReference),
                registered_first_at: currentMasterdataRegisteredFirstAt,
                type: currentMasterdataOwnershipType,
            },
        });
    };

    const persistAssetChanges = async (status = asset.status) => {
        const updatedAsset = getUpdatedAsset(status);

        await createAndUpdateAssetAndTag(unsavedTags, updatedAsset);
        setUnsavedTags([]);
        await performMasterdataUpdate(updatedAsset);
    };

    const archiveAndUpdateAsset = () =>
        persistAssetChanges(AssetStatus.archived).finally(() => setShowArchiveAssetConfirmationDialog(false));

    const activateAndUpdateAsset = () => persistAssetChanges(AssetStatus.active);

    const onTagListChange = useCallback((selectedTags: SelectedTag[]) => {
        setUnsavedTags(selectedTags.filter(tag => tag.status !== SelectedTagStatus.unchanged));
    }, []);

    const createAndUpdateAssetAndTag = async (tagsToUpdate: SelectedTag[], assetToUpdate: Asset) => {
        const newTags = tagsToUpdate.filter(currentTag => currentTag.status === SelectedTagStatus.created);
        const updatedTags = tagsToUpdate.filter(
            currentTag =>
                currentTag.status === SelectedTagStatus.added || currentTag.status === SelectedTagStatus.removed
        );
        if (asset) {
            await createAssetTags(newTags, asset);
            await updateAsset(asset, assetToUpdate);
            await updateAssetTags(assetToUpdate, updatedTags);
        }
    };

    const handleTabClick = (event: React.MouseEvent, selectedAssetsListDetailTab: AssetsListDetailTab) => {
        event.preventDefault();
        event.stopPropagation();
        if (hasUnsavedChanges()) {
            // this fixes the behavior that the tag manager cannot display changes like tags added/removed
            // when it gets newly mounted
            toggleConfirmDialog(true, asset.id as string, () => {
                resetState(asset);
                toggleConfirmDialog(false, asset.id as string, onClose);
                changeTabAndUpdateUrl(selectedAssetsListDetailTab);
            });
        } else {
            changeTabAndUpdateUrl(selectedAssetsListDetailTab);
        }
    };
    const changeTabAndUpdateUrl = (assetsListDetailTab: AssetsListDetailTab) => {
        if (asset && assetsListDetailTab !== currentAssetDetailsTab) {
            changeAssetDetailsTab(assetsListDetailTab as AssetsListDetailTab);
        }
    };

    const handleAssetDetailTabClick = (e: React.MouseEvent) => {
        handleTabClick(e, AssetsListDetailTab.ASSET_DETAILS);
    };
    const handleDeviceListTabClick = (e: React.MouseEvent) => {
        handleTabClick(e, AssetsListDetailTab.DEVICE_LIST);
    };
    const handleTagListTabClick = (e: React.MouseEvent) => {
        handleTabClick(e, AssetsListDetailTab.TAG_LIST);
    };

    const handleStatusActionClick = () => {
        toggleUnsavedAssetChanges(false);
        if (asset.status === AssetStatus.active) {
            setShowArchiveAssetConfirmationDialog(true);
        } else {
            void activateAndUpdateAsset();
        }
    };

    const handleDeleteAsset = () => {
        return deleteAsset(updatedAsset);
    };

    const handleMasterdataOwnershipTermInMonthsChange = (termInMonths: number | null | undefined) => {
        setMasterdataOwnershipTermInMonths(termInMonths);
        if (!isNullOrEmpty(currentMasterdataOwnershipStartDate)) {
            const startDate = moment(currentMasterdataOwnershipStartDate, DATE_FORMAT);
            const endDate = startDate.add(termInMonths, 'M');
            setMasterdataOwnershipEndDate(endDate.format(DATE_FORMAT));
            setMasterdataOwnershipEndDateChangeJustTriggered(true);
        }
    };

    const handleMasterdataOwnershipStartDate = (ownershipStartDate: string | null | undefined) => {
        setMasterdataOwnershipStartDate(ownershipStartDate);
        if (
            !isNullOrEmpty(currentMasterdataOwnershipEndDate) &&
            !isNullOrEmpty(ownershipStartDate) &&
            endDateIsSameOrAfterStartDateOrNull(ownershipStartDate, currentMasterdataOwnershipEndDate)
        ) {
            const startDate = moment(ownershipStartDate, DATE_FORMAT);
            const endDate = moment(currentMasterdataOwnershipEndDate, DATE_FORMAT);
            setMasterdataOwnershipTermInMonths(endDate.diff(startDate, 'months', false));
            setMasterdataOwnershipTermInMonthsChangeJustTriggered(true);
        }
    };
    const handleMasterdataOwnershipEndDate = (ownershipEndDate: string | null | undefined) => {
        setMasterdataOwnershipEndDate(ownershipEndDate);
        if (
            !isNullOrEmpty(currentMasterdataOwnershipStartDate) &&
            !isNullOrEmpty(ownershipEndDate) &&
            endDateIsSameOrAfterStartDateOrNull(currentMasterdataOwnershipStartDate, ownershipEndDate)
        ) {
            const startDate = moment(currentMasterdataOwnershipStartDate, DATE_FORMAT);
            const endDate = moment(ownershipEndDate, DATE_FORMAT);
            setMasterdataOwnershipTermInMonths(endDate.diff(startDate, 'months', false));
            setMasterdataOwnershipTermInMonthsChangeJustTriggered(true);
        }
    };

    const triggerTimeoutMS = 1500;
    useEffect(() => {
        if (!currentMasterdataOwnershipEndDateChangeJustTriggered) {
            return;
        }

        const timeoutId = setTimeout(() => setMasterdataOwnershipEndDateChangeJustTriggered(false), triggerTimeoutMS);
        return () => clearTimeout(timeoutId);
    }, [currentMasterdataOwnershipEndDateChangeJustTriggered]);

    useEffect(() => {
        if (!currentMasterdataOwnershipTermInMonthsChangeJustTriggered) {
            return;
        }

        const timeoutId = setTimeout(
            () => setMasterdataOwnershipTermInMonthsChangeJustTriggered(false),
            triggerTimeoutMS
        );
        return () => clearTimeout(timeoutId);
    }, [currentMasterdataOwnershipTermInMonthsChangeJustTriggered]);

    const getTabContent = (assetsListDetailTab: AssetsListDetailTab) => {
        switch (assetsListDetailTab) {
            case AssetsListDetailTab.ASSET_DETAILS:
                return (
                    <AssetDetails
                        asset={asset}
                        currentAssetLicensePlate={currentAssetLicensePlate}
                        currentAssetLicensePlateCountryCode={currentAssetLicensePlateCountryCode}
                        currentAssetName={currentAssetName}
                        currentAssetIdentification={currentAssetIdentification}
                        currentAssetBrand={currentAssetBrand}
                        currentMasterdataAdr={currentMasterdataAdr}
                        currentMasterdataAirFreightSuitability={currentMasterdataAirFreightSuitability}
                        currentMasterdataComment={currentMasterdataComment}
                        currentMasterdataCustomsBond={currentMasterdataCustomsBond}
                        currentMasterdataCostCenter={currentMasterdataCostCenter}
                        currentMasterdataFuelType={currentMasterdataFuelType}
                        currentMasterdataLeasingRate={currentMasterdataLeasingRate}
                        currentMasterdataNumberOfAxes={currentMasterdataNumberOfAxes}
                        currentMasterdataVehicleModel={currentMasterdataVehicleModel}
                        currentMasterdataEngineType={currentMasterdataEngineType}
                        currentMasterdataOwnershipEndDate={currentMasterdataOwnershipEndDate}
                        currentMasterdataOwnershipEndDateChangeJustTriggered={
                            currentMasterdataOwnershipEndDateChangeJustTriggered
                        }
                        currentMasterdataOwnershipStartDate={currentMasterdataOwnershipStartDate}
                        currentMasterdataOwnershipTermInMonths={currentMasterdataOwnershipTermInMonths}
                        currentMasterdataOwnershipTermInMonthsChangeJustTriggered={
                            currentMasterdataOwnershipTermInMonthsChangeJustTriggered
                        }
                        currentMasterdataOwnershipType={currentMasterdataOwnershipType}
                        currentMasterdataReference={currentMasterdataReference}
                        currentMasterdataRegisteredFirstAt={currentMasterdataRegisteredFirstAt}
                        currentMasterdataTires={currentMasterdataTires}
                        identificationHasError={identificationHasError}
                        locale={intl.locale}
                        onAssetIdentificationChange={setCurrentAssetIdentification}
                        onAssetBrandChange={setCurrentAssetBrand}
                        onAssetLicensePlateChange={setCurrentAssetLicensePlate}
                        onAssetLicensePlateCountryCodeChange={setCurrentAssetLicensePlateCountryCode}
                        onAssetNameChange={setCurrentAssetName}
                        onMasterdataAdr={setMasterdataAdr}
                        onMasterdataAirFreightSuitability={setMasterdataAirFreightSuitability}
                        onMasterdataComment={setMasterdataComment}
                        onMasterdataCustomsBond={setMasterdataCustomsBond}
                        onMasterdataCostCenter={setMasterdataCostCenter}
                        onMasterdataFuelType={setMasterdataFuelType}
                        onMasterdataLeasingRate={setMasterdataLeasingRate}
                        onMasterdataNumberOfAxes={setMasterdataNumberOfAxes}
                        onMasterdataVehicleModel={setMasterdataVehicleModel}
                        onMasterdataEngineType={setMasterdataEngineType}
                        onMasterdataOwnershipType={setMasterdataOwnershipType}
                        onMasterdataOwnershipEndDate={handleMasterdataOwnershipEndDate}
                        onMasterdataOwnershipStartDate={handleMasterdataOwnershipStartDate}
                        onMasterdataReference={setMasterdataReference}
                        onMasterdataRegisteredFirstAt={setMasterdataRegisteredFirstAt}
                        onMasterdataOwnershipTermInMonths={handleMasterdataOwnershipTermInMonthsChange}
                        onMasterdataTires={setMasterdataTires}
                        setIdentificationHasError={setIdentificationHasError}
                        associatedDevices={associatedDevices}
                    />
                );
            case AssetsListDetailTab.TAG_LIST:
                return (
                    <TagManagerWrapper
                        accountId={asset.account_id}
                        assetTags={asset.tags}
                        onTagListChange={onTagListChange}
                        availableTags={availableTags}
                        useCustomTags={true}
                        customMessage={'intl-msg:asset-list-detail.tag-manager.message'}
                    />
                );
            case AssetsListDetailTab.DEVICE_LIST:
                return <DeviceList />;
        }
    };

    const tabLiClasses = (assetsListDetailTab: AssetsListDetailTab) => {
        return currentAssetDetailsTab === assetsListDetailTab ? 'active' : '';
    };

    const sideBarContent = (
        <React.Fragment>
            <ul className='nav nav-pills nav-pills-filled nav-pills-filled-condensed nav-justified shadow-subtle margin-bottom-20'>
                <li className={tabLiClasses(AssetsListDetailTab.ASSET_DETAILS)}>
                    {/* biome-ignore lint/a11y/useValidAnchor: suppress out of convenience during migration to biome */}
                    {/** biome-ignore lint/a11y/noStaticElementInteractions: suppress out of convenience during migration to biome */}
                    <a data-cy='asset-details-asset-tab' onClick={handleAssetDetailTabClick}>
                        {AssetDetails.headingText}
                    </a>
                </li>
                <li className={tabLiClasses(AssetsListDetailTab.TAG_LIST)}>
                    {/* biome-ignore lint/a11y/useValidAnchor: suppress out of convenience during migration to biome */}
                    {/** biome-ignore lint/a11y/noStaticElementInteractions: suppress out of convenience during migration to biome */}
                    <a data-cy='asset-details-tags-tab' onClick={handleTagListTabClick}>
                        <FormattedMessage id={'intl-msg:asset.tags'} />
                    </a>
                </li>
                <li className={tabLiClasses(AssetsListDetailTab.DEVICE_LIST)}>
                    {/* biome-ignore lint/a11y/useValidAnchor: suppress out of convenience during migration to biome */}
                    {/** biome-ignore lint/a11y/noStaticElementInteractions: suppress out of convenience during migration to biome */}
                    <a data-cy='asset-details-devices-tab' onClick={handleDeviceListTabClick}>
                        <FormattedMessage id={'intl-msg:asset.devices'} />
                    </a>
                </li>
            </ul>
            {getTabContent(currentAssetDetailsTab)}
        </React.Fragment>
    );
    const updatedAsset = getUpdatedAsset();
    return (
        <>
            <Sidebar
                title={currentAssetName}
                closed={false}
                position={'right'}
                onClose={onCloseCallback}
                footer={
                    <SidebarFooter
                        saveButtonDisabled={isSaveButtonDisabled}
                        modalDeleteButtonDisabled={assetDeleting}
                        loadingOnSave={assetUpdating || assetTagsUpdating}
                        displayDeleteDialog={displayDeleteDialog}
                        deleteAsset={handleDeleteAsset}
                        openDeleteDialog={openDeleteDialog}
                        closeDeleteDialog={closeDeleteDialog}
                        deletionErrorMessageCode={deletionErrorMessageCode}
                        updatedAsset={updatedAsset}
                        currentAssetStatus={asset.status}
                        statusActionDisabled={isStatusActionDisabled}
                        onStatusActionClick={handleStatusActionClick}
                        submitButtonClick={submitButtonClick}
                        toggleUnsavedAssetChanges={toggleUnsavedAssetChanges}
                    />
                }
                resizable={true}
                minWidth={300}
                width={420}
                switchModeBreakpoint={SIDEBAR_BREAKPOINT}
                titleClassName={'padding-left-10'}
                bodyClassName={'padding-left-20 padding-right-20'}
            >
                {sideBarContent}
            </Sidebar>
            <ArchiveAssetConfirmationDialog
                asset={asset}
                show={showArchiveAssetConfirmationDialog}
                updateAsset={archiveAndUpdateAsset}
                disableConfirm={isStatusActionConfirmDisabled}
                abortArchivingAsset={() => setShowArchiveAssetConfirmationDialog(false)}
                isLatamTenant={isLatamTenant}
            />
        </>
    );
};
